/**
 * Server-side projections of the prompts and opt-in riders an attack COULD raise, computed
 * before a target is picked. Each reuses the engine's own gather/fire path rather than a
 * parallel walk over the overlay, so a projection can never drift from what actually fires.
 */

import type { EncounterState } from "@lancer-vtt/domain";
import { encounterGridMode, gridFamily, isRangedAttackAt, tagCell } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import { projectionDiceSource } from "../../dice";
import type { ActionMatch, AttackMatch, Handler, LoadedOverlay } from "../effects";
import {
  attackMatchAdmitsWeapon,
  combatantEffectIds,
  createResolutionLedger,
  gatherHandlersAt,
  gatherSystemHandlers,
  seedHandlers,
  seedPilotHandlers,
} from "../effects";
import type { GameEvent } from "../events/gameEvents";
import { fireAttackDeclared } from "./attack";
import type { DecisionOption, NeedsDecision, ResolveArgs } from "./types";
import { attackHandlerId, selectWeapon } from "./weapons";

/**
 * Discover the FIRST unanswered `answerKind:"options"` pre-roll `choose` prompt
 * `attackerId`'s declared-phase handlers would raise for `weapon`, by reusing
 * {@link fireAttackDeclared}'s own gather rather than walking predicates in parallel — which
 * is what keeps this projection from drifting out of step with the real fire path.
 *
 * Probes with **no real target** (a bare, non-unit cell keeps `fireAttackDeclared`'s own
 * `primaryTarget` `undefined`, exactly like a Blast aimed at open ground) and an **empty**
 * recorded-choices map, so ANY unanswered `choose` halts immediately with a
 * `NeedsDecisionSignal` before its option branches ever run — target-independent by
 * construction, which is the scope guard: attacker+weapon predicates only, never
 * target-gated. Returns `undefined` when nothing halts, or when the one thing that does is a
 * `"direction"` decision (a spatial pick — out of scope; it falls through to the live prompt
 * like every other un-projected case).
 *
 * **Discovers at most the first `choose` — first-halt-wins is deliberate, not a bug to fix
 * here.** Confirming a SECOND attacker-only `choose` exists would mean actually running the
 * first choice's own option effects, and those can be target-dependent (Walking Armory's
 * Shock option fans out over a target-centered disk) — which would break the
 * target-independence this whole projection rests on. The un-projected second `choose` falls
 * through to the ordinary live prompt. Shipped content keeps one declared-phase `choose` per
 * feature: Walking Armory's ranks share rank 1's one ammo `choose`, whose rank-2 options are
 * gated on the carrier holding `t_walking_armory:2`.
 *
 * **Crosses dice-drawing atoms; never reads what they roll.** The intent is that this draws
 * no dice at all — an unanswered `choose` halts before any downstream roll — but at least one
 * shipped clause violates that: `t_siege_specialist:2`'s declared-phase `force_save` draws a
 * d20 before its sibling `move_self_toward_target`. So the probe's dice input is
 * {@link projectionDiceSource}, a constant, non-exhausting, discovery-only source; a
 * dice-drawing atom is crossed and its recorded steps thrown away with everything else (this
 * function never reads `steps`). The residual risk is pinned, not hand-waved: a clause that
 * nested a `choose` inside a `force_save`'s `on_pass`/`on_fail` or a `roll`'s
 * `on_hit`/`on_miss` would make discovery depend on the constant's face, and
 * `packs/core-rulebook/entries/_helpers/declaredDiceChooseNesting.pin.test.ts` fails the day
 * that shape ships.
 *
 * Throws for a `weapon` that {@link selectWeapon} rejects (not on the loadout, or a loadout
 * weapon System Trauma destroyed), like {@link previewOptInRiders}; the caller skips destroyed
 * weapons before probing. `fireAttackDeclared` only ever *catches* a `NeedsDecisionSignal`; any
 * other throw — a genuine engine bug in the traversal — propagates straight out of here
 * uncaught. The caller wraps each call in its own `catch` and logs loudly, so a discovery bug
 * in one weapon's authored content can never break a whole broadcast.
 */
/** Named arguments for {@link previewAttackDeclaredChoice} (kept under the repo's `max-params-3` gate). */
export type PreviewAttackDeclaredChoiceArgs = {
  state: EncounterState;
  attackerId: string;
  /** The weapon **instance key** ({@link import("@lancer-vtt/domain").weaponInstanceKey}) to probe. */
  weapon: string | undefined;
  overlay: LoadedOverlay;
};

export function previewAttackDeclaredChoice(
  args: PreviewAttackDeclaredChoiceArgs,
): { promptId: string; prompt: string; options: DecisionOption[] } | undefined {
  const { state, attackerId, weapon, overlay } = args;
  const attacker = state.combatants[attackerId];
  if (!attacker) return undefined;
  // `optional-effect-prompts` Phase 3 Slice B: assume acceptance of every `requiresOptIn`
  // rider this attacker+weapon carries (`previewOptInRiders` — the SAME enumeration the
  // checkbox surface itself uses, so this can never diverge from what's actually offered).
  // Necessary because a gated "you may…" clause (Walking Armory's `t_walking_armory:1`) can
  // itself CONTAIN the `choose` this function exists to discover (the ammo pick) — with no
  // assumed answer, `fireTrigger`'s `requiresOptIn` gate (`triggerBus.ts`) declines the
  // clause outright and the inner `choose` never runs, so the preview would silently regress
  // from "preselect before target selection" to a mid-resolution prompt. Assuming acceptance
  // is the reading consistent with D1 (`decisions/0127`): a checkbox is a STANDING
  // CONDITIONAL intent ("if it lands and qualifies, do it"), not a refusal-by-default; this
  // preview answers "which prompts COULD this attack raise", which is exactly what an opted-
  // in rider could raise. If the player later actually declines the checkbox, the recorded
  // ammo answer this preview causes is provably inert: `interpretChoose` (`interpret.ts`)
  // only writes the `ResolutionLedger` (and only a decline-gated `chose` predicate reads
  // that ledger) when the `choose` atom itself runs — and a declined `requiresOptIn` gate
  // skips `interpretEffect` for the whole clause, so the inner `choose` never executes and
  // the ledger entry is never written. Second gather of the same handlers `previewOptInRiders`
  // itself already re-derives (a perf cost, not a correctness one — both calls are pure/no-
  // dice); a future perf pass could thread the already-computed riders through as a parameter
  // instead (`backend/src/encounterPreRollProjection.ts`'s `projectWithPreRollChoices` calls both functions
  // per weapon today and could compute once), but a required extra parameter risks a call site
  // silently omitting it and reintroducing this exact regression, so correct-by-default wins.
  const optInChoices: Record<string, string> = {};
  for (const rider of previewOptInRiders(args)) optInChoices[rider.key] = "true";
  const { halt: declared } = dryRunAttackDeclared({ state, attackerId, intent: { weapon, ...(Object.keys(optInChoices).length > 0 ? { choices: optInChoices } : {}) }, overlay });
  if (declared === undefined) return undefined; // no unanswered choose — nothing to project
  // Scope guard: this preview's contract is "the first OPTIONS-shaped choose, or nothing" —
  // never surface a spatial "direction" halt to the caller (an inspector checkbox row has no
  // way to render a map-click prompt). `granted-movement-allowances` Phase 1.1 risk check:
  // **as of the `reactionToward` pre-answer above, this branch is unreachable in practice.**
  // Every currently-authored "direction" halt (`interpretMoveSelfTowardTarget` AND
  // `interpretForcedMove`, both in `effects/interpret.ts`) gates on the SAME shared
  // `ctx.toward === undefined && ctx.interactive` condition this probe now always pre-answers
  // — so `fireAttackDeclared` can no longer throw an `answerKind:"direction"` signal from
  // anywhere this function calls it. Traced, not assumed: grepped every `answerKind:
  // "direction"` construction site in `packages/rules/src` and confirmed both read `ctx.toward`.
  // Kept anyway as deliberate belt-and-braces — the return type promises `options`-only, and a
  // future direction-halt site that (wrongly) doesn't consult `ctx.toward` should still fail
  // safe here rather than leak a malformed decision to the inspector. There is deliberately NO
  // pin for this branch: the prior one (`previewAttackDeclaredChoice.test.ts`'s "declared-phase
  // DIRECTION halt is still out of scope" case) passed for this exact reason BEFORE the fix
  // and kept passing AFTER it for an unrelated one (the halt stopped firing at all, so the
  // `!("kind" in declared)` branch above returns first) — a hollow pin, deleted rather than
  // left green-for-the-wrong-reason. See that test file's replacement coverage instead.
  if (declared.answerKind !== "options") return undefined;
  return { promptId: declared.promptId, prompt: declared.prompt, options: declared.options };
}

/**
 * **The declared phase as a dry run** (`event-engine-to-hit` 5.1, F-TH1): `fireAttackDeclared` on
 * a target-independent probe intent, with the given preselected answers, over a ledger that is
 * thrown away. It returns the first unanswered prompt it halts on, if any, and the events the
 * declaration raised (its charge costs). {@link previewAttackDeclaredChoice} reads the halt, the
 * attack forecast (`attackForecast.ts`) reads the costs: one probe, two readers.
 */
export function dryRunAttackDeclared(args: {
  state: EncounterState;
  attackerId: string;
  /** The probed intent's own fields: the weapon, its profile, its preselected answers. */
  intent: Pick<RequestAttack, "weapon" | "profile" | "choices">;
  overlay: LoadedOverlay;
}): { halt?: NeedsDecision; events: GameEvent[] } {
  const { state, attackerId, intent, overlay } = args;
  const attacker = state.combatants[attackerId];
  if (!attacker) return { events: [] };
  const gridMode = encounterGridMode(state);
  const probeArgs: ResolveArgs = {
    state,
    intent: {
      type: "request_attack",
      attacker: attacker.id,
      // Deliberately NOT a unit target — keeps `primaryTarget` `undefined` inside
      // `fireAttackDeclared`, the same target-independent shape a cell-aimed area attack
      // already gets. The cell itself is arbitrary; nothing here reads it.
      target: { kind: "cell", cell: { grid: "square", x: 0, y: 0 } },
      accuracy: 0,
      ...intent,
      // `granted-movement-allowances` Phase 1.1 regression fix: pre-answer the declared-
      // phase spatial DIRECTION halt (`interpretMoveSelfTowardTarget` / `interpretForcedMove`
      // in `effects/interpret.ts` — both gate on `ctx.toward === undefined && ctx.interactive`,
      // fed from `intent.reactionToward` at this function's own `fireAttackDeclared` call,
      // mirroring the declared-phase fire site's `...(intent.reactionToward !==
      // undefined ? { toward: … } : {})` spread — {@link import("./attack").fireAttackDeclared}).
      // Before this fix, an attacker carrying
      // BOTH `t_siege_specialist:2` (a declared-phase `move_self_toward_target{toward:
      // "chosen"}` self-push) AND `t_walking_armory:1` (a declared-phase `choose` — the ammo
      // pick) could lose the ammo prompt entirely: if Siege Specialist's handler happened to
      // gather first (data-dependent — `pilotEffectIds` array order is unsorted), its
      // unanswered direction halt threw BEFORE traversal ever reached Walking Armory's
      // sibling `choose`, and the `answerKind !== "options"` scope guard below swallowed the
      // whole probe — silently discarding a REAL `choose` prompt the live resolve() flow
      // would still eventually raise. Answering it here lets the move atom fall straight
      // through to `travelDestination`, record a `forced_move` step (this probe never reads
      // `steps` — the recorded destination is thrown away, exactly like the target cell
      // above), and continue on to the next handler, the same outcome a client that had
      // already answered the prompt would get. Draws no dice either way (`interpret.ts`'s
      // move atoms never roll).
      //
      // Grid-tagged via `tagCell`/`gridFamily` rather than a bare `{ x: 0, y: 0 }`: a
      // MISMATCHED tag would not actually crash here even so — verified by tracing
      // `travelDestination` (`effects/spatialAnswerer.ts`): a hex `World`'s `line()` fed a
      // square-shaped `Cell` reads `undefined` q/r/s off it, every hex-algebra step degrades
      // to `NaN`, the ray comes back length-0, and `walkAndBackoff`'s `maxSteps = min(dist, 0)`
      // loop never runs — `travelDestination` returns the MOVER'S OWN position, a harmless
      // no-op, never a throw (the same "warn-and-allow" posture `cellGridMismatch` documents
      // at every other site in this file, just without the warning since this probe's steps
      // are discarded anyway). But there's no reason to lean on that incidental
      // degrade-safely path when a correctly-tagged origin cell costs nothing — both helpers
      // are already imported for the `cellGridMismatch` call sites elsewhere in this file.
      reactionToward: tagCell(gridMode, gridFamily(gridMode) === "square" ? { x: 0, y: 0 } : { q: 0, r: 0, s: 0 }),
    },
    // NOT always avoided: the design intent is that an unanswered `choose` halts before
    // any downstream roll, but at least one shipped clause (`t_siege_specialist:2`'s
    // declared-phase `force_save`) draws before its sibling `choose`-free move handler.
    // `projectionDiceSource()` is a constant, non-exhausting, discovery-only source
    // (`packages/rules/src/dice.ts`) that crosses a draw like that instead of throwing —
    // this function never reads `steps`, so the face it yields cannot affect what is
    // returned (see its own doc + the corpus pin for the one residual risk and how it's
    // guarded).
    input: { dice: projectionDiceSource() },
    effects: { overlay, interactive: true },
  };
  // A probe: its steps are thrown away, so are any events raised into its ledger.
  const ledger = createResolutionLedger("discards");
  const declared = fireAttackDeclared(probeArgs, attacker, ledger);
  return { ...("kind" in declared ? { halt: declared } : {}), events: ledger.events ?? [] };
}

/** Named arguments for {@link previewOptInRiders} — mirrors {@link PreviewAttackDeclaredChoiceArgs} exactly. */
export type PreviewOptInRidersArgs = PreviewAttackDeclaredChoiceArgs;

/**
 * One `requiresOptIn` rider (`optional-effect-prompts` Phase 1, `decisions/0127`) the given
 * attacker+weapon combination could raise. `key` is the full recorded-choice answer key
 * (`"optIn:" + name`) so a caller never reconstructs it by string-concatenation.
 */
export type OptInRiderPreview = {
  key: string;
  name: string;
  /** The authored initial checkbox state (`TriggeredEffect.optInDefault`), defaulted `false` while the field is transitionally optional (Phase 2a). */
  optInDefault: boolean;
};

/**
 * Statically enumerate the `requiresOptIn` riders the attacker carries whose weapon `match`
 * admits `weapon` — the Phase 2 checkbox-surface projection helper (`optional-effect-prompts`
 * tracker, Phase 2 recon point 2/3). Unlike {@link previewAttackDeclaredChoice}, this does
 * **not** fire a synthetic event: `requiresOptIn` riders live at `on_hit`/`on_crit` (target-
 * gated, outcome-gated), so a probe firing wouldn't discover them without a real target and a
 * real roll — exactly the thing a pre-declared checkbox must NOT require. Instead this reuses
 * the ENGINE'S OWN gather composition (`seedHandlers`/`seedPilotHandlers`/`gatherSystemHandlers`,
 * byte-identical to the real on_hit fire's gather block — {@link
 * import("./attackTriggerFires").fireHitTriggers}) and
 * filters the resulting handlers to `requiresOptIn` clauses whose `match`'s WEAPON-derived
 * facets (`attackMatchAdmitsWeapon`, `family.ts`) admit this weapon — deliberately never
 * evaluating `condition` or any outcome-dependent `match` facet (`hit`/`crit`/`natural20`/…):
 * under D1 a checkbox is a **standing conditional intent** ("if it lands and qualifies, do
 * it"), so this projection never predicts whether a rider will actually fire — `fireTrigger`'s
 * own full `evaluateMatch` + `condition` settle that at real fire time, same as always.
 *
 * Draws no dice (pure data lookup over the overlay + the attacker's frozen loadout), and throws
 * only for a `weapon` that {@link selectWeapon} rejects: one not on that loadout, or a loadout
 * weapon System Trauma destroyed. The caller (`backend`'s per-broadcast projection) skips
 * destroyed weapons before probing and still wraps each call defensively, matching
 * `previewAttackDeclaredChoice`'s own contract.
 */
export function previewOptInRiders(args: PreviewOptInRidersArgs): OptInRiderPreview[] {
  const { state, attackerId, weapon, overlay } = args;
  const attacker = state.combatants[attackerId];
  if (!attacker) return [];
  const baseWeapon = selectWeapon(attacker, weapon, undefined);
  // No tech-attack modeling here, mirroring `previewAttackDeclaredChoice`'s own scope (the
  // intent shape it probes with is never a tech attack either) — `tech` stays `false`.
  const tech = false;
  // `attackHandlerId` is the SINGLE derivation of the composite/action id every attack-family
  // fire site must use (see its own doc in `weapons.ts`) — this preview is itself an
  // attack-family site (it filters `family: "attack"` handlers), so it must call the helper
  // rather than hardcode `baseWeapon.id`, or it would silently drift from the real fire path
  // the moment either derivation changed. The minimal `RequestAttack`-shaped probe intent below
  // carries only the two fields the helper reads (`tech`, `weapon`); with `tech: false` and no
  // melee action id, it resolves to `baseWeapon.id` — the "ordinary weapon attack" case — same
  // as a real declaration would for this weapon.
  const probeIntent: RequestAttack = { type: "request_attack", attacker: attacker.id, target: { kind: "unit", id: attacker.id }, accuracy: 0, weapon, tech };
  const actionId = attackHandlerId(probeIntent, baseWeapon.id);
  const weaponFacts = { weaponType: baseWeapon.type, mount: baseWeapon.mount, ranged: !tech && isRangedAttackAt(baseWeapon.range, null), tech, actionId };
  const handlerWeapon = { id: baseWeapon.id, ...(baseWeapon.tags ? { tags: baseWeapon.tags } : {}) };
  // The SAME gather composition the real on_hit fire uses (weapon-borne + pilot + system
  // handlers, all family:"attack") — reusing it here, rather than a parallel scan over the
  // overlay, is what keeps this projection from silently diverging from the actual fire path.
  const handlers: Handler[] = [
    ...seedHandlers({ state, attackerId: attacker.id, weapon: handlerWeapon, overlay }),
    ...seedPilotHandlers({
      carrierId: attacker.id,
      pilotEffectIds: combatantEffectIds(attacker),
      overlay,
    }),
    ...gatherSystemHandlers({ state, carrierId: attacker.id, overlay, family: "attack" }),
  ];
  const out: OptInRiderPreview[] = [];
  for (const h of handlers) {
    if (h.effect.optional === true) continue; // never surface a reaction-class rider here (same exclusion the real fire path applies)
    if (h.effect.requiresOptIn !== true || !h.effect.name) continue;
    const match = h.effect.match;
    if (match !== undefined && match.family === "attack" && !attackMatchAdmitsWeapon(match as AttackMatch, weaponFacts)) continue;
    out.push({ key: `optIn:${h.effect.name}`, name: h.effect.name, optInDefault: h.effect.optInDefault ?? false });
  }
  return out;
}

/** Named arguments for {@link previewOptInActionRiders} (kept under the repo's `max-params-3` gate). */
export type PreviewOptInActionRidersArgs = {
  state: EncounterState;
  carrierId: string;
  overlay: LoadedOverlay;
};

/**
 * Statically enumerate the `requiresOptIn` riders `carrierId` could raise off an `on:"action"`
 * clause (`optional-effect-prompts` Phase 4 Slice 2a) — the action-family SIBLING of
 * {@link previewOptInRiders}, not a widening of it. **`previewOptInRiders` cannot reach these
 * riders at all** — it *requires* a `weapon` and gathers/filters on `family: "attack"`
 * (`attackMatchAdmitsWeapon`), whereas an `on:"action"` clause (`pg_mobility_hardsuit`/
 * `ms_rapid_burst_jump_jet_system`'s Boost-Flying riders, `match: { family: "action", actionId:
 * "boost" }`) carries neither a weapon nor an attack-family `match` — wrong family, no weapon,
 * structurally invisible to the whole attack-family machinery. This is a new sibling, not a
 * widened `previewOptInRiders`.
 *
 * **Reuses the ENGINE'S OWN gather** — `gatherHandlersAt({ state, carrierId, overlay, family:
 * "action" })`, the byte-identical call {@link resolveActionTakenTrigger} fires with — so this
 * enumeration can never drift from what the real fire path actually gathers (the same
 * anti-divergence discipline {@link previewOptInRiders}'s own doc describes, reusing the real
 * gather composition rather than a parallel scan over the overlay). Filters to `requiresOptIn`
 * clauses carrying a `name`, and skips `optional: true` (never surface a reaction-class rider
 * here — the same exclusion {@link previewOptInRiders} applies). Deliberately never evaluates
 * `condition` — same D1 "standing conditional intent" reasoning as {@link previewOptInRiders}:
 * this projects which riders COULD be offered, not whether one will actually fire.
 *
 * **Keyed by `actionId`, read off each rider's OWN `match.actionId` — never hardcoded.** A
 * clause's `match.actionId` is a `MatchStringField` (any-of — {@link ActionMatch}'s own doc): a
 * rider is filed under EVERY id it names, so a rider authored against two action ids surfaces
 * under both keys.
 *
 * **The `actionId: undefined` case, decided explicitly (not left to fall through):** a clause
 * whose `match` names no `actionId` at all — either `match` itself is absent, or present with
 * `actionId` unset — matches every action under {@link ActionMatch}'s own any-of/unrestricted
 * semantics. But this projection's whole POINT is to key one checkbox row per action id for a
 * UI that renders a control next to a specific action button; there is no single button to
 * attach an action-wildcard rider to. Rather than silently vanish (an enumerator returning `[]`
 * for the wrong reason — indistinguishable from correctly finding nothing) or silently attach
 * it to an arbitrary key (misrepresenting what the clause actually gates), such a rider is
 * **deliberately skipped**, with this comment as the standing record of that choice. No
 * authored content exercises this path today — the two live `on:"action"` carriers both name
 * `actionId: "boost"` explicitly — so if a genuinely action-wildcard `requiresOptIn` rider is
 * ever authored, revisit this call rather than assuming it's still moot (a wildcard checkbox
 * surface, or a documented owed-to-the-developer note, are both on the table).
 *
 * Never throws (a pure data lookup over the overlay + `carrierId`'s live handlers) — the caller
 * (`backend`'s per-broadcast projection) still wraps each call in its own try/catch, matching
 * {@link previewOptInRiders}'s own contract: a discovery bug in one carrier's authored content
 * must never break the whole broadcast.
 */
export function previewOptInActionRiders(args: PreviewOptInActionRidersArgs): Record<string, OptInRiderPreview[]> {
  const { state, carrierId, overlay } = args;
  const handlers = gatherHandlersAt({ state, carrierId, overlay, family: "action" });
  const out: Record<string, OptInRiderPreview[]> = {};
  for (const h of handlers) {
    if (h.effect.optional === true) continue; // never surface a reaction-class rider here (mirrors previewOptInRiders)
    if (h.effect.requiresOptIn !== true || !h.effect.name) continue;
    const match = h.effect.match;
    const actionIdField = match !== undefined && match.family === "action" ? (match as ActionMatch).actionId : undefined;
    if (actionIdField === undefined) continue; // action-wildcard match — deliberately skipped, see this function's own doc
    const ids = Array.isArray(actionIdField) ? actionIdField : [actionIdField];
    const preview: OptInRiderPreview = { key: `optIn:${h.effect.name}`, name: h.effect.name, optInDefault: h.effect.optInDefault ?? false };
    for (const id of ids) (out[id] ??= []).push(preview);
  }
  return out;
}
